import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import ScrollButton from "common/Buttons/ScrollButton";
import React, {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Virtual } from "swiper/modules";
import Flex from "common/Boxes/Flex";
import { Swiper as SwiperClass } from "swiper/types";
import useMeasure from "utils/hooks/useMeasure";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import { typography } from "@shared/common/Themes/typography";
import { VIRTUAL_CARDS_LIST_PADDING } from "@shared/common/constants";
import RouterLink from "common/Buttons/RouterLink";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface VirtualCardsListProps<T extends { id: number }, E = unknown> {
  cardsData: T[] | undefined;
  extraCardsData?: E;
  loading?: boolean;
  cardHeight: number;
  cardWidth: number;
  CardComponent: ComponentType<VirtualCardComponentProps<T, E>>;
  title?: ReactNode;
  seeAllLink?: string;
  hideScrollButtons?: boolean;
  /** Phones: show the edge of the next card, so the row reads as one that
      scrolls sideways. */
  peekNext?: boolean;
}

interface VirtualCardComponentProps<T, E> {
  data: T;
  extraData?: E;
  index: number;
}

const VirtualCardsList = <T extends { id: number }, E = unknown>({
  cardsData,
  extraCardsData,
  loading,
  cardHeight,
  cardWidth,
  CardComponent,
  title,
  seeAllLink,
  hideScrollButtons,
  peekNext,
}: VirtualCardsListProps<T, E>) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const [swiperProgress, setSwiperProgress] = useState<
    SwiperProgress | undefined
  >();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const swiperRef = useRef<SwiperRef | undefined>(undefined);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const swiperHeight = cardHeight + 2 * VIRTUAL_CARDS_LIST_PADDING;
  const swiperWidth = Math.max(width - 2 * VIRTUAL_CARDS_LIST_PADDING, 0);
  const fittingSlides = Math.max(swiperWidth / cardWidth, 1);
  const slidesPerView =
    peekNext && isMobile && (cardsData?.length ?? 0) > fittingSlides
      ? Math.floor(fittingSlides) + PEEK_SLIDE_FRACTION
      : fittingSlides;
  const useVirtual = (cardsData?.length ?? 0) > VIRTUAL_LIST_MIN_CARDS;
  const swiperKey: SwiperKey = useVirtual ? "virtual" : "standard";
  const getSwiper = useCallback(() => {
    const swiperData = swiperRef.current;
    if (!swiperData || swiperData.key !== swiperKey) return;

    return swiperData.swiper.destroyed ? undefined : swiperData.swiper;
  }, [swiperKey]);
  const cards = useMemo(
    () =>
      cardsData?.map((cardData, index) => {
        return (
          <SwiperSlide key={cardData.id} virtualIndex={index}>
            <CardSlot>
              <CardComponent
                data={cardData}
                extraData={extraCardsData}
                index={index}
              />
            </CardSlot>
          </SwiperSlide>
        );
      }),
    [CardComponent, cardsData, extraCardsData]
  );

  useEffect(() => {
    const swiper = getSwiper();
    if (!swiper || !cardsData || !width) return;

    if (useVirtual && swiper.virtual) {
      swiper.virtual.update(true);
    }

    swiper.update();
  }, [cardsData, cardWidth, getSwiper, useVirtual, width]);

  useEffect(() => {
    setSwiperProgress(undefined);
  }, [swiperKey]);

  const scrollButtons = !isMobile && cardsData && (
    <ScrollButtonsWrapper>
      {seeAllLink && (
        <RouterLink to={seeAllLink} style={{ margin: "0 12px" }}>
          {t("labels.see_all")}
        </RouterLink>
      )}
      {!hideScrollButtons && (
        <>
          <ScrollButton
            disabled={swiperProgress === SwiperProgress.START}
            onClick={() => getSwiper()?.slidePrev()}
            isNextButton={false}
          />
          <ScrollButton
            disabled={
              swiperProgress === SwiperProgress.END ||
              cardsData?.length <= slidesPerView
            }
            onClick={() => getSwiper()?.slideNext()}
            isNextButton={true}
          />
        </>
      )}
    </ScrollButtonsWrapper>
  );

  const scrollButtonsWithTitle = (
    <Flex
      className={VIRTUAL_CARDS_TITLE_CLASS}
      style={{
        flex: 1,
        margin: `0 ${VIRTUAL_CARDS_LIST_PADDING}px`,
      }}
    >
      {typeof title === "string" ? (
        <Title level={3} $weight={typography.fontWeights.BOLD}>
          {title}
        </Title>
      ) : (
        title
      )}
      {scrollButtons}
    </Flex>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={loading || !cardsData}
      spinnerMarginTop={100}
      wrap={true}
      wrapStyle={{ minHeight: swiperHeight }}
      backgroundColor={theme.colors.layoutBgColor}
    >
      {title ? scrollButtonsWithTitle : scrollButtons}
      <div
        style={{
          height: swiperHeight,
          width: "100%",
          overflow: "hidden",
        }}
        ref={measureRef}
      >
        <Swiper
          key={swiperKey}
          direction="horizontal"
          slidesPerView={slidesPerView}
          modules={SWIPER_MODULES}
          virtual={
            useVirtual
              ? {
                  enabled: true,
                  addSlidesAfter: 2,
                  addSlidesBefore: 2,
                }
              : undefined
          }
          freeMode={{
            enabled: true,
            sticky: false,
            momentumBounce: false,
          }}
          style={{
            width,
            padding: `${VIRTUAL_CARDS_LIST_PADDING}px ${VIRTUAL_CARDS_LIST_PADDING}px ${
              VIRTUAL_CARDS_LIST_PADDING + 8
            }px ${VIRTUAL_CARDS_LIST_PADDING}px`,
          }}
          onInit={(swiper: SwiperClass) => {
            swiperRef.current = { key: swiperKey, swiper };
            if (!cardsData || !width) return;

            if (useVirtual && swiper.virtual) {
              swiper.virtual.update(true);
            }

            swiper.update();
          }}
          onBeforeDestroy={(swiper: SwiperClass) => {
            if (swiperRef.current?.swiper === swiper) {
              swiperRef.current = undefined;
            }
          }}
          onProgress={(_: unknown, progress: number) => {
            if (progress === 0) {
              setSwiperProgress(SwiperProgress.START);
            } else if (progress === 1) {
              setSwiperProgress(SwiperProgress.END);
            } else {
              setSwiperProgress(undefined);
            }
          }}
        >
          {cards}
        </Swiper>
      </div>
    </LoadingSpinnerOverlay>
  );
};

export default VirtualCardsList;

enum SwiperProgress {
  START,
  END,
}

type SwiperKey = "virtual" | "standard";

interface SwiperRef {
  key: SwiperKey;
  swiper: SwiperClass;
}

const ScrollButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: fit-content;
`;
const CardSlot = styled.div`
  > * {
    max-width: calc(100% - 16px);
    box-sizing: border-box;
  }
`;

const VIRTUAL_LIST_MIN_CARDS = 10;
/** How much of the next card shows (as a share of a slide). */
const PEEK_SLIDE_FRACTION = 0.12;
export const VIRTUAL_CARDS_TITLE_CLASS = "virtual-cards-title";
const SWIPER_MODULES = [Virtual, FreeMode];
